import { useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Search, Handshake, MapPin, Building2, ArrowRight } from 'lucide-react';
import { getCustomersPaged, getCustomerLocations } from '../services/customerApi';
import { useDebounce } from '@hooks/useDebounce';
import ResizablePanel from '@shared/components/common/ResizablePanel';
import { getInitials } from '@shared/utils/stringUtils';
import type { Customer, CustomerLocation } from '../types/clientClinic';
import { withXmId } from '@shared/utils/xmId';
import './CustomerLocationBrowserPage.css';

// Customer names may use "Last, First" formatting, so split on commas too.
const CUSTOMER_NAME_SEPARATOR = /[\s,]+/;

// On-scroll pagination page size for the customer list.
const CUSTOMER_PAGE_SIZE = 50;

export default function CustomerLocationBrowserPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const customerListRef = useRef<HTMLDivElement>(null);

  // Server-side search + pagination: the customer table is larger than any
  // sane single fetch, so each page (and the search) comes from the API.
  const debouncedSearch = useDebounce(searchQuery, 300);
  const {
    data: customerPages,
    isLoading: isLoadingCustomers,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: ['customers', 'paged', debouncedSearch],
    queryFn: ({ pageParam }) => getCustomersPaged(debouncedSearch, pageParam, CUSTOMER_PAGE_SIZE),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.meta.page < last.meta.totalPages ? last.meta.page + 1 : undefined),
  });

  const customers = customerPages?.pages.flatMap((p) => p.data) ?? [];
  const totalCustomers = customerPages?.pages[0]?.meta.totalCount ?? 0;

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // New search -> the query key changes and pagination restarts server-side.
    setSearchQuery(e.target.value);
    customerListRef.current?.scrollTo({ top: 0 });
  };

  const handleCustomerListScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, clientHeight, scrollHeight } = e.currentTarget;
    if (hasNextPage && !isFetchingNextPage && scrollTop + clientHeight >= scrollHeight - 200) {
      void fetchNextPage();
    }
  };

  const isSearching = isLoadingCustomers;

  // Fetch locations for selected customer
  const {
    data: locations = [],
    isLoading: isLoadingLocations,
  } = useQuery<CustomerLocation[]>({
    queryKey: ['customerLocations', selectedCustomer?.id],
    queryFn: () => getCustomerLocations(selectedCustomer!.id),
    enabled: !!selectedCustomer,
  });

  const handleCustomerSelect = (customer: Customer) => {
    setSelectedCustomer(customer);
  };

  const handleLocationClick = (locationId: number) => {
    navigate(`/client-relationships?locationId=${locationId}`);
  };

  // Left panel: Customer list with search
  const leftPanel = (
    <div className="customer-list-panel">
      <div className="customer-list-header">
        <div className="customer-list-title-row">
          <span className="customer-list-title">Customers</span>
          {!isSearching && (
            <span className="customer-list-count">{totalCustomers}</span>
          )}
        </div>
        <div className="search-box">
          <Search size={16} className="search-icon" />
          <input
            type="text"
            placeholder="Search customers..."
            aria-label="Search customers"
            value={searchQuery}
            onChange={handleSearchChange}
            className="search-input"
          />
        </div>
      </div>

      <div className="customer-list" ref={customerListRef} onScroll={handleCustomerListScroll}>
        {isSearching && (
          <div className="loading-state">
            <p>Loading customers...</p>
          </div>
        )}

        {!isSearching && customers.length === 0 && searchQuery.length >= 1 && (
          <div className="empty-state">
            <p>No customers found matching "{searchQuery}"</p>
          </div>
        )}

        {!isSearching && customers.length === 0 && searchQuery.length < 1 && (
          <div className="empty-state">
            <p>No customers available</p>
          </div>
        )}

        {!isSearching && customers.map((customer) => {
          const isSelected = selectedCustomer?.id === customer.id;
          return (
            <button
              key={customer.id}
              type="button"
              className={`customer-card${isSelected ? ' selected' : ''}`}
              onClick={() => handleCustomerSelect(customer)}
              aria-pressed={isSelected}
            >
              <span className="customer-avatar" aria-hidden="true">
                {getInitials(customer.name, CUSTOMER_NAME_SEPARATOR) || '—'}
              </span>
              <span className="customer-info">
                <span className="customer-name">{withXmId(customer.name, customer.xmId)}</span>
                {customer.accountNumber && (
                  <span className="customer-account">{customer.accountNumber}</span>
                )}
              </span>
            </button>
          );
        })}

        {!isSearching && hasNextPage && (
          <div className="customer-list-more" aria-live="polite">
            {isFetchingNextPage
              ? 'Loading more...'
              : `Showing ${customers.length} of ${totalCustomers} — scroll for more`}
          </div>
        )}
      </div>
    </div>
  );

  // Right panel: Location grid for selected customer
  const rightPanel = (
    <div className="location-grid-panel">
      <div className="panel-header">
        <div className="panel-header-text">
          <h2>{selectedCustomer ? withXmId(selectedCustomer.name, selectedCustomer.xmId) : 'Customer Facilities'}</h2>
          <p className="panel-header-subtitle">
            {selectedCustomer
              ? `${locations.length} ${locations.length === 1 ? 'facility' : 'facilities'}`
              : 'Pick a customer from the list to view their facilities'}
          </p>
        </div>
      </div>

      <div className="location-grid">
        {!selectedCustomer && (
          <div className="empty-state">
            <div className="empty-icon-wrap">
              <Building2 size={36} className="empty-icon" />
            </div>
            <h3>No customer selected</h3>
            <p>Pick a customer from the list on the left to see the facilities they own.</p>
          </div>
        )}

        {selectedCustomer && isLoadingLocations && (
          <div className="loading-state">
            <p>Loading facilities...</p>
          </div>
        )}

        {selectedCustomer && !isLoadingLocations && locations.length === 0 && (
          <div className="empty-state">
            <div className="empty-icon-wrap">
              <MapPin size={36} className="empty-icon" />
            </div>
            <h3>No facilities</h3>
            <p>This customer does not have any facilities recorded yet.</p>
          </div>
        )}

        {selectedCustomer && !isLoadingLocations && locations.length > 0 && (
          <div className="location-table-wrapper">
            <table className="location-table">
              <thead>
                <tr>
                  <th>Facility Name</th>
                  <th>Address</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {locations.map((location) => (
                  <tr key={location.id}>
                    <td className="location-name">{withXmId(location.name, location.xmId)}</td>
                    <td className="location-address">
                      {location.city && location.state ? (
                        `${location.city}, ${location.state}`
                      ) : (
                        <span className="text-muted">No address</span>
                      )}
                    </td>
                    <td className="text-right">
                      <button
                        type="button"
                        className="btn-find-clinics"
                        onClick={() => handleLocationClick(location.id)}
                      >
                        Find / Assign
                        <ArrowRight size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="customer-location-browser-page">
      <div className="page-header">
        <div className="page-title-row">
          <Handshake size={28} />
          <h1>Customer Facilities</h1>
        </div>
        <p className="page-description">
          Select a customer to view their facilities, then click a facility to find nearby clinics
        </p>
      </div>

      <ResizablePanel
        leftPanel={leftPanel}
        rightPanel={rightPanel}
        defaultLeftWidth={350}
        minLeftWidth={300}
        maxLeftWidth={500}
        storageKey="customer-browser-panel-width"
      />
    </div>
  );
}
